<template>
  <v-container>
    <v-data-table-server
      v-model:items-per-page="pagination.limit"
      :items="branches"
      :headers="headers"
      :items-length="count"
      :loading="loading"
      :search="pagination.q"
      item-key="id"
      @update:options="handleLimitChange">
      <template v-slot:top>
        <v-text-field
          v-model="pagination.q"
          class="ma-2"
          density="compact"
          placeholder="Search setting..."
          hide-details></v-text-field>
      </template>
      <template v-slot:[`item.action`]="{ item }">
        <v-btn icon variant="text" small color="primary" @click="openModal(item)">
          <v-icon icon="mdi-pencil"></v-icon>
        </v-btn>
      </template>
    </v-data-table-server>
    <v-dialog v-model="modalOpen" max-width="500px">
      <v-card>
        <v-card-title>{{ selectedSetting.key }}</v-card-title>
        <v-form ref="form" @submit.prevent="onSubmit">
          <v-card-text>
            <v-text-field v-model="selectedSetting.value" label="Value" :rules="[rules.required]" />
          </v-card-text>
          <v-card-actions>
            <v-btn color="secondary" @click="canceled">Cancel</v-btn>
            <v-btn color="primary" @click="onSubmit">Save</v-btn>
          </v-card-actions>
        </v-form>
      </v-card>
    </v-dialog>
  </v-container>
</template>
<script setup lang="ts">
import { useSettingsStore } from '@/stores/modules/admin';
import { IPaginationDTO, ISettingsDTO, PaginationDTO, SettingsDTO } from '@/dtos';
import { computed, ref, watch } from 'vue';
import type { VDataTableServer, VForm } from 'vuetify/components';
import { useUpdateStatusStore } from '@/stores';
import { rules } from '@/helpers';
import { SNACKSTATUS } from '@/stores/util';

const emit = defineEmits(['snack']);
const settingsStore = useSettingsStore();
const statusStore = useUpdateStatusStore();
const pagination = ref<IPaginationDTO>(new PaginationDTO());
const { data, isLoading } = settingsStore.getAllSettings(pagination);
const modalOpen = ref<boolean>(false);

const branches = computed(() => data.value?.data ?? []);
const loading = computed(() => isLoading.value);
const count = computed(() => data.value?.count ?? 0);
const form = ref<InstanceType<typeof VForm> | null>();
const selectedSetting = ref<ISettingsDTO>(new SettingsDTO());
const headers = [
  { title: 'Key', key: 'key' },
  { title: 'Value', key: 'value' },
  { title: 'Edit', key: 'action' },
];

const handleLimitChange = (value: VDataTableServer) => {
  pagination.value.buildPagination(value);
};

const canceled = () => {
  selectedSetting.value = new SettingsDTO();
  modalOpen.value = false;
};

const onSubmit = () => {
  settingsStore.update.mutateAsync(selectedSetting.value);
};

const openModal = (item: ISettingsDTO) => {
  selectedSetting.value = new SettingsDTO(item);
  modalOpen.value = true;
};

watch(
  () => statusStore.getStatus(`setting-${selectedSetting.value.id}`),
  (value: SNACKSTATUS | undefined) => {
    if (!value) {
      return;
    }
    const key = `setting-${selectedSetting.value.id}`;
    emit('snack', 'Setting', value, statusStore.getError(key));
    canceled();
  },
);
</script>
